import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { Calendar, CheckCircle2, Eye, MapPin, Rocket } from 'lucide-react';
import { Badge, Button, ConfirmDialog, PageHeader, Spinner } from '../components/ui';
import { adminEventService, type AdminEvent } from '../services/adminEventService';
import { announceEventWorkspaceUpdated } from '../utils/eventWorkspace';

export default function EventPublishPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<AdminEvent | null>(null);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eventId) {
      setError('Event ID is missing.');
      setLoading(false);
      return;
    }
    let active = true;
    adminEventService.get(eventId)
      .then((loaded) => {
        if (active) setEvent(loaded);
      })
      .catch((loadError) => {
        if (active) setError(loadError instanceof Error ? loadError.message : 'Could not load event.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [eventId]);

  async function publish() {
    if (!eventId) return;
    setPublishing(true);
    setError(null);
    try {
      const updated = await adminEventService.update(eventId, { status: 'published' });
      setEvent(updated);
      announceEventWorkspaceUpdated(updated);
      setConfirmOpen(false);
    } catch (publishError) {
      setError(publishError instanceof Error ? publishError.message : 'Could not publish event.');
      setConfirmOpen(false);
    } finally {
      setPublishing(false);
    }
  }

  if (loading) return <div className="flex justify-center py-24"><Spinner className="h-6 w-6 text-gray-500" /></div>;
  if (!event || !eventId) return <div role="alert" className="m-8 rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error ?? 'Event not found.'}</div>;

  const attendeeBaseUrl = import.meta.env.VITE_ATTENDEE_URL || (import.meta.env.DEV ? 'http://localhost:5173' : '');
  const canPublish = (event.effective_role === 'admin' || event.effective_role === 'manager') && event.status === 'draft';

  return (
    <div className="mx-auto max-w-5xl p-8">
      <PageHeader
        title="Review & publish"
        description="Check the attendee experience before making this event available."
        action={attendeeBaseUrl ? (
          <a href={`${attendeeBaseUrl}/event-preview/${eventId}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-lg border border-gray-700 bg-gray-800 px-4 py-2 text-sm font-medium text-gray-100 hover:bg-gray-700">
            <Eye size={14} /> Preview attendee app
          </a>
        ) : undefined}
      />

      {error && <p role="alert" className="mb-5 rounded-xl border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">{error}</p>}

      <section className="rounded-2xl border border-gray-800 bg-gray-900 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-gray-800 pb-5">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <h2 className="text-lg font-semibold text-white">{event.name}</h2>
              <Badge variant={event.status === 'draft' ? 'warning' : 'success'}>{event.status[0].toUpperCase() + event.status.slice(1)}</Badge>
            </div>
            <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm text-gray-400">
              <span className="inline-flex items-center gap-2"><Calendar size={14} />{event.date_start} – {event.date_end}</span>
              <span className="inline-flex items-center gap-2"><MapPin size={14} />{event.location}</span>
            </div>
          </div>
        </div>

        <div className="grid gap-3 py-5 sm:grid-cols-3">
          <Link to={`/events/${eventId}/content`} className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 hover:border-sky-500/30">
            <p className="text-sm font-medium text-white">Content & schedule</p>
            <p className="mt-1 text-xs text-gray-500">Review program records</p>
          </Link>
          <Link to={`/events/${eventId}/app-setup`} className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 hover:border-sky-500/30">
            <p className="text-sm font-medium text-white">Attendee experience</p>
            <p className="mt-1 text-xs text-gray-500">Review navigation and home</p>
          </Link>
          <Link to={`/events/${eventId}/access`} className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 hover:border-sky-500/30">
            <p className="text-sm font-medium text-white">People & access</p>
            <p className="mt-1 text-xs text-gray-500">Review entry rules</p>
          </Link>
        </div>

        {event.status === 'published' ? (
          <div className="flex items-center gap-3 rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4 text-emerald-300">
            <CheckCircle2 size={20} />
            <div>
              <p className="text-sm font-semibold">This event is published.</p>
              <p className="mt-0.5 text-xs text-emerald-300/70">Future content and experience updates can still be saved normally.</p>
            </div>
          </div>
        ) : canPublish ? (
          <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-sky-500/20 bg-sky-500/5 p-4">
            <div>
              <p className="text-sm font-semibold text-white">Ready to make the event available?</p>
              <p className="mt-1 text-xs text-gray-400">Publishing changes the event status. It does not prevent later edits.</p>
            </div>
            <Button icon={<Rocket size={14} />} onClick={() => setConfirmOpen(true)}>Publish event</Button>
          </div>
        ) : (
          <p className="rounded-xl border border-gray-800 bg-gray-950/50 p-4 text-sm text-gray-400">This event is currently {event.status}. No publication action is available.</p>
        )}
      </section>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void publish()}
        loading={publishing}
        intent="primary"
        title="Publish this event?"
        description="Attendees with access will be able to use the published event. You can continue editing it afterward."
        confirmLabel="Publish now"
      />
    </div>
  );
}
